import * as React from "react";
import styled from "@emotion/styled";
import { Monaco, OnMount } from "@monaco-editor/react";
import { palette, space } from "@stedi/dls/constants";
import { fontWeight } from "@stedi/dls/styles";
import { isJsonataEvaluationError, JsonataEvaluationError } from "@stedi/transformers-jsonata";
import type { editor as monacoEditor } from "monaco-editor";
import * as monacoAPI from "monaco-editor/esm/vs/editor/editor.api";
import { setupMonacoJSONata } from "../../lib/jsonata/monaco";
import { JSONToJSONSchema } from "../../lib/jsonSchema/jsonSchema";
import { setupMonacoJSONSchema } from "../../lib/jsonSchema/monaco";
import { BaseEditor } from "./BaseEditor";
import { makeLazyEditor } from "./LazyEditor";

// generated on https://remixicon.com/ from the `error-warning-fill` icon by passing in the value of palette.red10 and 14px as a size
const errorIconDataURL = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='14' height='14'%3E%3Cpath fill='none' d='M0 0h24v24H0z'/%3E%3Cpath d='M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z' fill='rgba(205,88,130,1)'/%3E%3C/svg%3E")`;

const SBaseEditor = styled(BaseEditor)({
  ".jsonata-error-glyph": {
    background: "rgba(205, 88, 130, 0.1)",
    backgroundPositionX: space.s1,
    backgroundPositionY: 3,
    backgroundRepeat: "no-repeat",
    backgroundImage: errorIconDataURL,
  },

  ".jsonata-error-line": {
    background: "rgba(205, 88, 130, 0.1)",
  },

  ".jsonata-error-token": {
    ...fontWeight("bold"),
    color: palette.red10,
    textDecorationColor: `${palette.red10} !important`,
    textDecoration: "underline",
    textDecorationStyle: "wavy",
  },

  ".squiggly-error": {
    background: "none !important",
  },

  ".hover-row.status-bar": {
    display: "none !important",
  },
});

interface ExpressionEditorProps {
  defaultValue: string | undefined;
  source: string | undefined;
  error: Error | undefined;

  onChange: (value: string | undefined) => void;
  onMount?: (editorInstance: monacoEditor.IStandaloneCodeEditor, monaco: typeof monacoAPI) => void;
}

const ExpressionEditor = ({ defaultValue, error, onChange, onMount, source }: ExpressionEditorProps) => {
  const monacoEditorCleanupRef = React.useRef<VoidFunction>(() => null);
  const monacoJSONSchemaCleanupRef = React.useRef<VoidFunction>(() => null);

  React.useEffect(() => {
    return () => {
      monacoEditorCleanupRef.current();
      monacoJSONSchemaCleanupRef.current();
    };
  }, []);

  const monacoRef = React.useRef<Monaco>();
  const sourceRef = React.useRef<string | undefined>();

  React.useEffect(() => {
    // We are saving it in a ref to let onMount callback read the latest available version of the source,
    // otherwise it reads the one available as part of its initial closure.
    sourceRef.current = source;

    if (!monacoRef.current) return;

    monacoJSONSchemaCleanupRef.current();

    const rootJSONSchema = JSONToJSONSchema(parseSourceObject(source));
    monacoJSONSchemaCleanupRef.current = setupMonacoJSONSchema({
      monaco: monacoRef.current,
      rootJSONSchema,
      // TODO(vitorbal): look into whether we need to re-enable this
      scopedJSONSchema: undefined,
    });
  }, [source]);

  const editorInstanceRef = React.useRef<monacoAPI.editor.IStandaloneCodeEditor>();

  useApplyErrorDecorators({
    error,
    monaco: monacoRef.current,
    editorInstance: editorInstanceRef.current,
  });

  useApplyErrorMarkers({
    error,
    monaco: monacoRef.current,
    editorInstance: editorInstanceRef.current,
  });

  const handleOnMount: OnMount = (editorInstance, monaco) => {
    editorInstanceRef.current = editorInstance;
    monacoRef.current = monaco;
    // Setting up JSONata on onBeforeMount caused syntax highlighting to be applied
    // only after a key was pressed
    const monacoJSONataCleanup = setupMonacoJSONata({ monaco });
    const onContextMenuCleanup = editorInstance.onContextMenu(() => {
      void preloadFormatJSONata();
    });

    monacoEditorCleanupRef.current = () => {
      monacoJSONataCleanup();
      onContextMenuCleanup.dispose();
    };

    const rootJSONSchema = JSONToJSONSchema(parseSourceObject(sourceRef.current));
    monacoJSONSchemaCleanupRef.current = setupMonacoJSONSchema({
      monaco,
      rootJSONSchema,
      // TODO(vitorbal): look into whether we need to re-enable this
      scopedJSONSchema: undefined,
    });

    editorInstance.addAction({
      id: "format-jsonata",
      label: "Format JSONata",
      contextMenuGroupId: "1_modification",
      keybindings: [
        /**
         * For some reason, the default keybinding combo from vscode does not work in our MonacoEditor:
         * `monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF,`
         *
         * resorting to this as a compromise (a couple of other built-in actions are using F1 and F2 in Monaco):
         */
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.F4,
      ],
      run: formatEditorContents,
    });

    /**
     * This is a workaround to make the suggestions details box always appear by default.
     * Monaco doesn't expose a way to do this via their public API, so we have to dangerously
     * reach into the internals of the suggest widget to make this work.
     *
     * If you're interested, the relevant monaco modules are:
     * - node_modules/monaco-editor/esm/vs/editor/contrib/suggest/browser/suggestController.js
     * - node_modules/monaco-editor/esm/vs/editor/contrib/suggest/browser/suggestWidget.js
     */
    const suggestController = editorInstance.getContribution("editor.contrib.suggestController");
    // @ts-expect-error There're no type definitions for this widget
    const suggestWidget = suggestController?.widget?.value;

    // eslint-disable-next-line no-underscore-dangle
    suggestWidget?._setDetailsVisible?.(true);
    /**
     * End of suggestion details box workaround
     */

    onMount?.(editorInstance, monaco);
  };

  return (
    <SBaseEditor
      defaultValue={defaultValue}
      language="jsonata"
      onChange={(newValue) => onChange(newValue)}
      onMount={handleOnMount}
      options={monacoOptions}
    />
  );
};

const LazyExpressionEditor = makeLazyEditor(ExpressionEditor, "jsonata");

export { ExpressionEditor, LazyExpressionEditor };

/**
 * Preload the JSONata formatting function since it is very heavy.
 * The preload can be called safely multiple times. The browser will fetch the asset only once.
 */
const preloadFormatJSONata = () =>
  import(/* webpackChunkName: "prettier-plugin-jsonata" */ "@stedi/prettier-plugin-jsonata/dist/lib");
const lazyFormatJSONata = (toFormat: string) =>
  preloadFormatJSONata().then(({ formatJsonataSync }) => formatJsonataSync(toFormat));

const monacoOptions: monacoEditor.IStandaloneEditorConstructionOptions = {
  ariaLabel: "JSONata",
  folding: false,
  glyphMargin: true,
  // To make sure left padding is the same as on Source/Output editors with folding disabled
  lineDecorationsWidth: 3,
  lineNumbers: "off",
  suggestSelection: "first",
};

const emptyObject = {};

const parseSourceObject = (source: string | undefined) => {
  if (!source) {
    return emptyObject;
  }

  try {
    return JSON.parse(source);
  } catch {
    return emptyObject;
  }
};

const formatEditorContents = async (editorInstance: monacoEditor.ICodeEditor) => {
  const model = editorInstance.getModel();
  if (!model) return;

  const rawEditorContents = model.getValue();
  try {
    const formattedEditorContents = await lazyFormatJSONata(rawEditorContents);
    replaceEditorText(editorInstance, formattedEditorContents);
  } catch (_e) {
    // ignoring errors for now, as it is in a POC phase
  }
};

/**
 * The `editor.setValue` destroys the undo-stack.
 * This makes it impossible to undo changes using CTRL/CMD + Z after the `editor.setValue` is called.
 *
 * To make sure the undo-stack is not removed, the `executeEdits` is used to override editor current text.
 */
function replaceEditorText(editorInstance: monacoEditor.ICodeEditor, newText: string): void {
  if (!editorInstance) return undefined;

  const existingValue = editorInstance.getValue();
  if (existingValue === newText) {
    return;
  }

  editorInstance.executeEdits(existingValue, [
    {
      range: {
        endColumn: Infinity,
        endLineNumber: Infinity,
        startColumn: 0,
        startLineNumber: 0,
      },
      text: newText,
      forceMoveMarkers: true,
    },
  ]);
}

interface UseApplyErrorDecoratorsProps {
  error: Error | undefined;
  monaco: Monaco | undefined;
  editorInstance: monacoAPI.editor.IStandaloneCodeEditor | undefined;
}

const useApplyErrorDecorators = ({ editorInstance, error, monaco }: UseApplyErrorDecoratorsProps) => {
  const decorationsRef = React.useRef<string[]>([]);

  const decorators = React.useMemo(
    () =>
      getErrorDecorations({
        error,
        monaco,
        editorInstance,
      }),
    [editorInstance, error, monaco],
  );

  React.useEffect(() => {
    if (!editorInstance) return;

    decorationsRef.current = editorInstance.deltaDecorations(decorationsRef.current, decorators);
  }, [decorators, editorInstance]);
};

// defined to help memoization and avoid unnecessary re-execution of useEffects
const emptyDecorationsArray: monacoAPI.editor.IModelDeltaDecoration[] = [];

interface GetErrorDecorationsProps {
  error: Error | undefined;
  monaco: Monaco | undefined;
  editorInstance: monacoAPI.editor.IStandaloneCodeEditor | undefined;
}

const getErrorDecorations = ({
  editorInstance,
  error,
  monaco,
}: GetErrorDecorationsProps): monacoAPI.editor.IModelDeltaDecoration[] => {
  if (!monaco || !editorInstance) {
    return emptyDecorationsArray;
  }

  const editorInstanceModel = editorInstance.getModel();
  if (!editorInstanceModel) {
    return emptyDecorationsArray;
  }

  if (!error || !isJsonataEvaluationError(error)) {
    return emptyDecorationsArray;
  }

  const decorations: monacoAPI.editor.IModelDeltaDecoration[] = [];

  const errorLineRange = getErrorLineRange({ monaco, error, editorInstanceModel });
  if (errorLineRange !== undefined) {
    decorations.push({
      range: errorLineRange,
      options: {
        isWholeLine: true,
        glyphMarginClassName: "jsonata-error-glyph",
        className: "jsonata-error-line",
      },
    });
  }

  const errorTokenRange = getErrorTokenRange({ monaco, error, editorInstanceModel });
  if (errorTokenRange !== undefined) {
    decorations.push({
      range: errorTokenRange,
      options: {
        inlineClassName: "jsonata-error-token",
      },
    });
  }

  return decorations;
};

interface UseApplyErrorMarkersProps {
  error: Error | undefined;
  monaco: Monaco | undefined;
  editorInstance: monacoAPI.editor.IStandaloneCodeEditor | undefined;
}

const useApplyErrorMarkers = ({ editorInstance, error, monaco }: UseApplyErrorMarkersProps) => {
  const markers = React.useMemo(
    () =>
      getErrorMarkers({
        error,
        monaco,
        editorInstance,
      }),
    [editorInstance, error, monaco],
  );

  React.useEffect(() => {
    if (!monaco || !editorInstance) return;

    const editorInstanceModel = editorInstance.getModel();
    if (!editorInstanceModel) {
      return;
    }

    monaco.editor.setModelMarkers(editorInstanceModel, "JSONata", markers);
  }, [editorInstance, markers, monaco]);
};

// defined to help memoization and avoid unnecessary re-execution of useEffects
const emptyMarkersArray: monacoEditor.IMarkerData[] = [];

interface GetErrorMarkersProps {
  error: Error | undefined;
  monaco: Monaco | undefined;
  editorInstance: monacoAPI.editor.IStandaloneCodeEditor | undefined;
}

const getErrorMarkers = ({ editorInstance, error, monaco }: GetErrorMarkersProps): monacoEditor.IMarkerData[] => {
  if (!monaco || !editorInstance) {
    return emptyMarkersArray;
  }

  const editorInstanceModel = editorInstance.getModel();
  if (!editorInstanceModel) {
    return emptyMarkersArray;
  }

  if (!error || !isJsonataEvaluationError(error)) {
    return emptyMarkersArray;
  }

  const markers: monacoEditor.IMarkerData[] = [];

  const errorTokenRange = getErrorTokenRange({ monaco, error, editorInstanceModel });
  if (errorTokenRange !== undefined) {
    markers.push({
      ...errorTokenRange,
      message: `Error: ${error.message}`,
      severity: monaco.MarkerSeverity.Error,
    });
  }

  return markers;
};

interface GetErrorLineRangeProps {
  monaco: Monaco;
  error: JsonataEvaluationError;
  editorInstanceModel: monacoAPI.editor.IModel;
}

const getErrorLineRange = ({
  editorInstanceModel,
  error,
  monaco,
}: GetErrorLineRangeProps): monacoAPI.Range | undefined => {
  if (error.position === undefined) {
    return undefined;
  }

  const errorPosition = editorInstanceModel.getPositionAt(error.position);

  return new monaco.Range(
    errorPosition.lineNumber,
    editorInstanceModel.getLineFirstNonWhitespaceColumn(errorPosition.lineNumber),
    errorPosition.lineNumber,
    editorInstanceModel.getLineLastNonWhitespaceColumn(errorPosition.lineNumber),
  );
};

interface GetErrorTokenRangeProps {
  monaco: Monaco;
  error: JsonataEvaluationError;
  editorInstanceModel: monacoAPI.editor.IModel;
}

const getErrorTokenRange = ({
  editorInstanceModel,
  error,
  monaco,
}: GetErrorTokenRangeProps): monacoAPI.Range | undefined => {
  if (error.position === undefined || error.token === undefined) {
    return undefined;
  }

  const errorPosition = editorInstanceModel.getPositionAt(error.position);
  const errorTokenStartPosition = editorInstanceModel.getPositionAt(error.position - error.token.toString().length);

  const errorTokenRange = new monaco.Range(
    errorTokenStartPosition.lineNumber,
    errorTokenStartPosition.column,
    errorPosition.lineNumber,
    errorPosition.column,
  );
  const errorTokenStringValue = editorInstanceModel.getValueInRange(errorTokenRange);

  /**
   * JSONata errors for errors related to functions are being misplaced by 1 char,
   * see https://github.com/jsonata-js/jsonata/pull/570 for details
   */
  const isMisplacedErrorForFunction = errorTokenStringValue.endsWith("(") && errorTokenStringValue.length > 1;
  if (!isMisplacedErrorForFunction) {
    return errorTokenRange;
  }

  return new monaco.Range(
    errorTokenStartPosition.lineNumber,
    errorTokenStartPosition.column - 1,
    errorPosition.lineNumber,
    errorPosition.column - 1,
  );
};
